import {
  FEATURE_FLAGS_CACHE_EXPIRATION,
  type FeatureFlagKey,
  type IFeatureFlag,
  StorageKey,
} from '../constants';
import { getStorage, removeStorage, setStorage } from './localStorage';
import { Logger } from './logger';

export interface IFeatureFlagsCacheData {
  features: Partial<Record<FeatureFlagKey, boolean>>;
  timestamp: number;
}

const isCacheExpired = (timestamp: number): boolean =>
  Date.now() - timestamp > FEATURE_FLAGS_CACHE_EXPIRATION;

export const getFeatureFlagsFromCache = (): IFeatureFlagsCacheData | null => {
  try {
    const cacheData = getStorage<IFeatureFlagsCacheData>(
      StorageKey.FEATURE_FLAGS
    );

    if (!cacheData) return null;

    if (isCacheExpired(cacheData.timestamp)) {
      clearFeatureFlagsCache();
      return null;
    }

    return cacheData;
  } catch (error) {
    Logger.error('Error retrieving feature flags from cache:', error);
    clearFeatureFlagsCache();
    return null;
  }
};

export const getFeatureFlagFromCache = (
  flagKey: FeatureFlagKey
): IFeatureFlag | null => {
  const cacheData = getFeatureFlagsFromCache();
  if (!cacheData) return null;

  return {
    feature: flagKey,
    active: cacheData.features[flagKey] ?? false,
  };
};

export const saveFeatureFlagsToCache = (
  features: Partial<Record<FeatureFlagKey, boolean>>
): void => {
  try {
    const cacheData: IFeatureFlagsCacheData = {
      features,
      timestamp: Date.now(),
    };
    setStorage(StorageKey.FEATURE_FLAGS, cacheData);
  } catch (error) {
    Logger.error('Error saving feature flags to cache:', error);
  }
};

export const clearFeatureFlagsCache = (): void => {
  try {
    removeStorage(StorageKey.FEATURE_FLAGS);
  } catch (error) {
    Logger.error('Error clearing feature flags cache:', error);
  }
};
